import React from "react";
import PropTypes from "prop-types";

import FullScreenButton from "./buttons/FullScreenButton";
import { ShareButton } from "./buttons/ShareButton";
import { GoBackButton } from "./buttons/GoBackButton";
import { CloseStickyPlayerButton } from "./buttons/CloseStickyPlayerButton";
import { StyledTopControls, TopControlsWrapper } from "./index.stitches";
import { ButtonWrapper } from "./buttons/ButtonWrapper";
import { useVideoDataContext } from "../orca/contexts/VideoDataContext";
import { useStickyVideoContext } from "../orca/contexts/StickyVideoContext";
import {
  getCanonicalUrl,
  getIsMiniPlayer,
  isExcludeVideoElement
} from "./utils";
import { useOrcaCommonContext } from "../orca/contexts/OrcaCommonContext";

export const TopControls = ({
  config,
  isVertical,
  closeCarousel,
  setSelected
}) => {
  const { videoData } = useVideoDataContext();
  const { isOpenSticky } = useStickyVideoContext();
  const {
    playerSize,
    setIsFullscreen,
    isFullscreen,
    closeFullscreen,
    showFullscreenBtn,
    closeStickyPlayer
  } = useOrcaCommonContext();

  const { isCarousel, allowShare = true } = config;

  const canonicalUrl = getCanonicalUrl(videoData);
  const isMiniPlayer = getIsMiniPlayer(playerSize, isFullscreen, isVertical);

  const showBackButton = (isFullscreen && isVertical) || isCarousel;
  const showCloseStickyButton = isOpenSticky && !isFullscreen;

  const isExcludeVideo = isExcludeVideoElement(videoData);
  const showShareButton =
    !isMiniPlayer && allowShare && canonicalUrl && !isExcludeVideo;

  const metaHeadline = videoData?.headlines.meta_title || "";
  const basicHeadline = videoData?.headlines.basic || "";
  const headline = metaHeadline || basicHeadline;

  return (
    <TopControlsWrapper isMiniPlayer={isMiniPlayer}>
      <StyledTopControls>
        {showBackButton && (
          <GoBackButton
            isCarousel={isCarousel}
            closeFullscreen={closeFullscreen}
            closeCarousel={closeCarousel}
          />
        )}
        <TopLeftButton
          {...{
            showCloseStickyButton,
            closeStickyPlayer,
            showShareButton,
            canonicalUrl,
            headline,
            setSelected,
            isMiniPlayer
          }}
        />
        <TopRightButton
          {...{
            isFullscreen,
            setIsFullscreen,
            isMiniPlayer,
            showFullscreenBtn
          }}
        />
      </StyledTopControls>
    </TopControlsWrapper>
  );
};

TopControls.propTypes = {
  /** boolean flag to denote if the video is vertical */
  isVertical: PropTypes.bool,
  /** Orca config prop. */
  config: PropTypes.object,
  /** If in carousel, this callback closes carousel active state */
  closeCarousel: PropTypes.func,
  setSelected: PropTypes.func
};

export const TopLeftButton = ({
  showCloseStickyButton,
  closeStickyPlayer,
  showShareButton,
  canonicalUrl,
  headline,
  setSelected
}) => {
  return (
    <ButtonWrapper>
      {showCloseStickyButton ? (
        <CloseStickyPlayerButton closeStickyPlayer={closeStickyPlayer} />
      ) : (
        showShareButton && (
          <ShareButton
            headline={headline}
            canonicalUrl={canonicalUrl}
            setSelected={setSelected}
          />
        )
      )}
    </ButtonWrapper>
  );
};

TopLeftButton.propTypes = {
  showCloseStickyButton: PropTypes.bool,
  closeStickyPlayer: PropTypes.func,
  showShareButton: PropTypes.bool,
  canonicalUrl: PropTypes.string,
  headline: PropTypes.string,
  setSelected: PropTypes.func
};

export const TopRightButton = ({
  isFullscreen,
  setIsFullscreen,
  isMiniPlayer,
  showFullscreenBtn
}) => {
  return (
    <ButtonWrapper>
      {showFullscreenBtn && (
        <FullScreenButton
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          isMiniPlayer={isMiniPlayer}
        />
      )}
    </ButtonWrapper>
  );
};

TopRightButton.propTypes = {
  isFullscreen: PropTypes.bool,
  setIsFullscreen: PropTypes.func,
  isMiniPlayer: PropTypes.bool,
  showFullscreenBtn: PropTypes.bool
};
